import React from 'react'
import ProductSwitcher from '../../ui/ProductSwitcher'
import classNames from 'classnames'
import UserMenu from './UserMenu'
import type { UserData } from '../../../services/User'

interface NavigationItem {
  name: string
  href: string
  icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  children?: NavigationItem[]
  // Optional small text appended to the right of the item name. Used
  // by the vendor portal for "1/2" submission progress; safe to leave
  // undefined elsewhere.
  badge?: string
  // Optional left-side dot, used today by vendor child items to
  // distinguish Submitted (emerald) from Pending (amber) at a glance.
  status?: 'submitted' | 'pending'
}

interface UserNavigationItem {
  name: string
  href?: string
  onClick?: () => void
}

interface NavbarProps {
  navigation: NavigationItem[]
  user: UserData
  currentProduct: string
  userNavigation: UserNavigationItem[]
  NotificationMenu?: React.ComponentType<{ dropdownPosition?: string }>
  pathname: string
  Link: React.ComponentType<{ to: string; className?: string; children?: React.ReactNode }>
  userNamePostfix?: string
}

function Navbar ({ navigation, user, currentProduct, userNavigation, NotificationMenu, pathname, Link, userNamePostfix }: NavbarProps) {

  const isActiveNav = (href: string) => pathname.includes(href)

  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-prussian-blue px-6" style={{ minWidth: '283px' }}>
      <div className="flex h-16 shrink-0 items-center justify-center">
        <ProductSwitcher currentUser={user} currentProduct={currentProduct}
          climateApps={window?.config?.climateApps as unknown as Record<string, string>} />
      </div>
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={classNames(
                      isActiveNav(item.href)
                        ? (item.children ? 'font-semibold text-white' : 'font-semibold text-bonfire-dark-dark')
                        : 'text-foggy-morning hover:font-semibold hover:bg-pacific-storm',
                      isActiveNav(item.href) && !item.children ? 'bg-white' : '',
                      'group flex gap-x-3 rounded-md p-2 text-sm/6 transition-colors duration-200',
                    )}
                  >
                    {item.icon && (
                      <item.icon
                        aria-hidden="true"
                        className={classNames(
                          isActiveNav(item.href) ? (
                            item.children ? 'text-white' : 'text-bonfire-dark-dark'
                          ) : 'text-polo-blue',
                          'size-6 shrink-0 transition-colors duration-200',
                        )}
                      />
                    )}
                    <span className="flex-1">{item.name}</span>
                    {item.badge && (
                      <span className="ml-auto inline-flex items-center rounded-full bg-pacific-storm/40 px-2 py-0.5 text-xs font-medium text-foggy-morning">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                  {item.children && (
                    <ul role="list" className="mt-1 space-y-1 max-h-[calc(100vh-22rem)] overflow-y-auto">
                      {item.children.map((subItem) => (
                        <li key={subItem.name}>
                          <Link
                            to={subItem.href}
                            className={classNames(
                              isActiveNav(subItem.href)
                                ? 'font-semibold bg-white text-bonfire-dark-dark'
                                : 'text-botticelli hover:font-semibold hover:bg-pacific-storm',
                              'group flex items-center gap-x-2 rounded-md p-2 pl-11 text-sm transition-colors duration-200',
                            )}
                          >
                            {subItem.status && (
                              <span
                                aria-hidden="true"
                                className={classNames(
                                  'size-2 shrink-0 rounded-full',
                                  subItem.status === 'submitted' ? 'bg-emerald-400' : 'bg-anzac-400',
                                )}
                              />
                            )}
                            <span className="truncate">{subItem.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </li>
          <li className="mt-auto mb-6 flex flex-col-reverse gap-y-4">

            <UserMenu currentProduct={currentProduct} userNavigation={userNavigation} userNamePostfix={userNamePostfix} />
            {/* No left padding here: the notification icon and the avatar are both 40px and
                must share the sidebar's left edge. A pl-2 pushed the mail icon 8px right of
                the avatar below it. */}
            <div className="max-w-12">
              {NotificationMenu && <NotificationMenu dropdownPosition="notBottomLeft" />}
            </div>

          </li>
        </ul>
      </nav>
    </div>
  )
}

export default Navbar
